import isEqual from 'lodash/isEqual';
import type {
  IAssistantAffectedGroup,
  IAssistantRow,
} from '@/interfaces/assistant.interface';
import type { IGhListCard, IGhListRow } from '../gpthelp-host-contract';
import { cardId } from './card-ids';
import { cardTextLength, clampText, fitText } from './card-text';
import type { ICardFormat } from './card-text';
import { changedGroupId, isNewGroupRow, recipeGroupId } from './row-presenters';

/*
 * The products that show a change of an option group (plan P3-4, D3). The
 * Action API lists them per changed or new group (`view.affected`: a group's
 * live links plus this proposal's product link rows, the first 50 by name,
 * the true count and how many keep their own rules). The diff card names
 * them under each group row and says the change is shared; the list card of
 * this module names every product.
 */

/** Host contract §3.3 caps of a list card. */
const TITLE_MAX = 80;
const INTRO_MAX = 240;
const FOOTNOTE_MAX = 160;
const ROW_TITLE_MAX = 160;
const ROW_META_MAX = 160;
/** A list card holds at most this many rows. */
const ROWS_MAX = 50;

/** A changed group is shared once this many products show it. */
const SHARED_MIN = 2;

/** What separates the footnote's parts. */
const FOOTNOTE_SEPARATOR = ' · ';

/** The changed groups that some product shows: a group no product uses is no news. */
function groupsInUse(
  affected: readonly IAssistantAffectedGroup[]
): IAssistantAffectedGroup[] {
  return affected.filter((group) => group.itemCount > 0);
}

/**
 * How many products show the change (plan P3-4): a group's own count; for
 * several, each product they list once plus each group's products past its
 * list (a group lists only its first 50 by name). A product that two long
 * groups both leave out of their lists counts twice, so the count never
 * says fewer products than the change reaches.
 */
export function affectedProductCount(
  affected: readonly IAssistantAffectedGroup[]
): number {
  const listed = new Set<number>();
  let unlisted = 0;
  for (const group of groupsInUse(affected)) {
    for (const item of group.items) listed.add(item.itemId);
    unlisted += Math.max(0, group.itemCount - group.items.length);
  }
  return listed.size + unlisted;
}

/**
 * Some existing group that changes shows in several products: the change
 * reaches all of them (plan P3-4). A group this proposal creates (no id yet)
 * reaches only the products it is added to, which its rows name already.
 */
export function changesSharedGroup(
  affected: readonly IAssistantAffectedGroup[]
): boolean {
  return affected.some(
    (group) => group.groupId !== null && group.itemCount >= SHARED_MIN
  );
}

/** The links of the changed groups that keep their own rules (plan P3-4: they do not change). */
export function ownRulesCount(
  affected: readonly IAssistantAffectedGroup[]
): number {
  return affected.reduce(
    (count, group) => count + Math.max(0, group.ownRules),
    0
  );
}

/**
 * By row key, the changed group of each group, choice and new-group row
 * (plan P3-4) and of each recipe of a choice (plan P5-3: its group's
 * products use it): an existing group by its id; a new one in the order of
 * the new-group rows, which is the order the API lists new groups in, or by
 * its name when that order does not hold. Rows without one are absent.
 */
export function affectedGroupByRow(
  rows: readonly IAssistantRow[],
  affected: readonly IAssistantAffectedGroup[]
): Map<string, IAssistantAffectedGroup> {
  const byRow = new Map<string, IAssistantAffectedGroup>();
  const created = affected.filter((group) => group.groupId === null);
  let next = 0;
  for (const row of rows) {
    if (isNewGroupRow(row)) {
      const inOrder = created[next];
      next += 1;
      const group =
        inOrder?.title === row.itemName
          ? inOrder
          : created.find((candidate) => candidate.title === row.itemName);
      if (group) byRow.set(row.key, group);
      continue;
    }
    const groupId = changedGroupId(row) ?? recipeGroupId(row);
    if (groupId === null) continue;
    const group = affected.find((candidate) => candidate.groupId === groupId);
    if (group) byRow.set(row.key, group);
  }
  return byRow;
}

/**
 * «Se usa en 3 productos: Cortado, Latte y Lágrima» within `room`
 * characters (plan P3-4): every name when they all fit, else the names added
 * by name while the text fits and the rest as «… y {n} más» (as the Menú's
 * group picker writes it); «Se usa en 12 productos» when not even one name
 * fits. Null for a group no product uses. Each name added makes the text
 * longer (it adds at least «, x» and the count it replaces loses at most a
 * digit or a separator), so the first name that does not fit ends the list.
 */
export function usedInText(
  group: IAssistantAffectedGroup,
  fmt: ICardFormat,
  room: number
): string | null {
  const count = group.itemCount;
  if (count <= 0) return null;
  const n = fmt.number(count);
  const names = group.items.map((item) => item.name);
  const usedIn = (list: string) =>
    fmt.tc('assistant.proposal.row.usedInNames', count, { n, list });
  if (names.length >= count) {
    const every = usedIn(fmt.list(names));
    if (cardTextLength(every) <= room) return every;
  }
  let fitting: string | null = null;
  for (let shown = 1; shown <= Math.min(names.length, count - 1); shown += 1) {
    const text = usedIn(
      fmt.t('menuPage.groups.picker.andMore', {
        list: names.slice(0, shown).join(', '),
        n: fmt.number(count - shown),
      })
    );
    if (cardTextLength(text) > room) break;
    fitting = text;
  }
  return (
    fitting ?? clampText(fmt.tc('menuPage.groups.usedIn', count, { n }), room)
  );
}

/**
 * «ahora se usa en 4 productos»: why a row of an existing option group went
 * stale while it still has the value the member saw. The API marks every
 * content row of a group that now reaches more products than the member saw
 * (plan D3, API P3-FIX F4) with that same value, which would otherwise read
 * as a new one. Null for any other row, or when the fresh view does not list
 * the group (`group`: the row's entry of `affectedGroupByRow`).
 */
export function grownGroupText(
  row: IAssistantRow,
  group: IAssistantAffectedGroup | undefined,
  fmt: ICardFormat
): string | null {
  if (
    !group ||
    row.state !== 'stale' ||
    changedGroupId(row) === null ||
    !isEqual(row.seenFrom ?? null, row.from)
  )
    return null;
  return fmt.tc('assistant.result.staleUsedIn', group.itemCount, {
    n: fmt.number(group.itemCount),
  });
}

/** One product of the list card: its groups, and whether it keeps its own rules. */
interface IAffectedProduct {
  itemId: number;
  name: string;
  groups: string[];
  ownRules: boolean;
}

/**
 * The products the list names, once each, by name (then id): every product a
 * changed group lists. One is marked as keeping its own rules when every
 * product of one of its groups does (the API counts them per group, never by
 * product, so a group where only some do marks none).
 */
function affectedProducts(
  groups: readonly IAssistantAffectedGroup[],
  locale: string
): IAffectedProduct[] {
  const products = new Map<number, IAffectedProduct>();
  for (const group of groups) {
    const everyOwnRules =
      group.ownRules > 0 && group.ownRules >= group.itemCount;
    for (const item of group.items) {
      const product = products.get(item.itemId) ?? {
        itemId: item.itemId,
        name: item.name,
        groups: [],
        ownRules: false,
      };
      product.groups.push(group.title);
      product.ownRules = product.ownRules || everyOwnRules;
      products.set(item.itemId, product);
    }
  }
  const collator = new Intl.Collator(locale);
  return Array.from(products.values()).sort(
    (a, b) => collator.compare(a.name, b.name) || a.itemId - b.itemId
  );
}

/**
 * «y 12 productos más · Los marcados tienen reglas propias.»: the products the
 * list leaves out, and who keeps their own rules (the marked ones, or how
 * many when none can be marked). Null when there is nothing to add.
 */
function footnoteOf(
  rest: number,
  marked: boolean,
  ownRules: number,
  fmt: ICardFormat
): string | null {
  const parts: string[] = [];
  if (rest > 0)
    parts.push(
      fmt.tc('assistant.affected.more', rest, { n: fmt.number(rest) })
    );
  if (marked) parts.push(fmt.t('assistant.affected.ownRules'));
  else if (ownRules > 0)
    parts.push(
      fmt.tc('assistant.proposal.note.groupOwnRules', ownRules, {
        n: fmt.number(ownRules),
      })
    );
  return parts.length
    ? clampText(parts.join(FOOTNOTE_SEPARATOR), FOOTNOTE_MAX)
    : null;
}

/**
 * «Se usa en 8 productos» (plan P3-4, D3): every product that shows a change
 * of an option group, once each and by name (at most 50, the rest counted),
 * with its groups when several change and a mark when it keeps its own
 * rules. It keeps the proposal's id, so a merged proposal of the same turn
 * replaces it. Null when no changed group has a product.
 */
export function affectedProductsCard(
  proposalId: string,
  affected: readonly IAssistantAffectedGroup[],
  fmt: ICardFormat
): IGhListCard | null {
  const groups = groupsInUse(affected);
  if (!groups.length) return null;
  const total = affectedProductCount(groups);
  const several = groups.length > 1;
  const products = affectedProducts(groups, fmt.locale).slice(0, ROWS_MAX);
  const rows = products.map((product) => {
    const row: IGhListRow = {
      key: `i${product.itemId}`,
      title: clampText(product.name, ROW_TITLE_MAX),
    };
    if (several) row.meta = clampText(product.groups.join(', '), ROW_META_MAX);
    if (product.ownRules) row.icon = 'info';
    return row;
  });
  const card: IGhListCard = {
    id: cardId('affected', proposalId),
    v: 1,
    type: 'list',
    title: clampText(
      fmt.tc('menuPage.groups.usedIn', total, { n: fmt.number(total) }),
      TITLE_MAX
    ),
    intro: several
      ? clampText(fmt.t('assistant.affected.introMany'), INTRO_MAX)
      : fitText(
          fmt,
          'assistant.affected.introOne',
          { group: groups[0].title },
          'group',
          INTRO_MAX
        ),
    rows,
  };
  const footnote = footnoteOf(
    total - rows.length,
    products.some((product) => product.ownRules),
    ownRulesCount(groups),
    fmt
  );
  if (footnote) card.footnote = footnote;
  return card;
}
